import { useCallback, useRef, useState } from 'react';

/**
 * Um card em edição por vez, e trocar de card **nunca** joga fora alteração em
 * silêncio.
 *
 * Antes, cada card guardava o próprio `editing`: dava para abrir 80 ao mesmo
 * tempo, e "editar a questão 4, mudar, clicar em editar a 8" perdia a mudança
 * sem dizer nada. Com o "quem está aberto" na página, existe um ponto onde a
 * pergunta pode ser feita.
 *
 * O card aberto registra `isDirty()` e `save()`. A guarda não sabe o que é uma
 * questão nem como salvá-la — só quando perguntar.
 */

export interface EditGuardRegistration {
  isDirty: () => boolean;
  /** `true` = salvou; `false` = não salvou (validação local ou erro do back). */
  save: () => Promise<boolean>;
}

export interface EditGuard {
  editingId: string | null;
  /** Abre um card para edição — ou pergunta antes, se o atual está sujo. */
  requestEdit: (id: string) => void;
  /** Fecha a edição atual (o card já cuidou do que tinha de cuidar). */
  close: () => void;
  /**
   * O card aberto se apresenta. Devolve o `unregister` — chamá-lo no unmount
   * importa: um card que sumiu (a questão foi excluída, o refetch a tirou da
   * prova) não pode continuar respondendo "tenho alteração não salva" por um
   * componente que já não existe, nem oferecer um `save()` que iria salvar uma
   * questão que se foi.
   */
  register: (id: string, registration: EditGuardRegistration) => () => void;
  /** Id que está esperando a resposta do modal. `null` = modal fechado. */
  pendingId: string | null;
  saving: boolean;
  /** "Cancelar": fecha o modal e continua na questão atual. */
  dismiss: () => void;
  /** "Descartar e continuar": abre a próxima, jogando fora o que foi digitado. */
  discardAndContinue: () => void;
  /** "Salvar e continuar": só troca se o salvamento der certo. */
  saveAndContinue: () => void;
}

export function useEditGuard(): EditGuard {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // `ref` e não estado: o card se registra durante o render, e mudar estado ali
  // seria um loop. A guarda só lê isto dentro de handlers.
  const registrations = useRef(new Map<string, EditGuardRegistration>());

  const register = useCallback(
    (id: string, registration: EditGuardRegistration) => {
      registrations.current.set(id, registration);
      return () => {
        // Só desregistra se ainda for esta registro — um re-register já
        // sobrescreveu, e apagar aí tiraria a do card vivo.
        if (registrations.current.get(id) === registration) {
          registrations.current.delete(id);
        }
      };
    },
    [],
  );

  const open = useCallback((id: string | null) => {
    setPendingId(null);
    setEditingId(id);
  }, []);

  const requestEdit = useCallback(
    (id: string) => {
      if (editingId === null || editingId === id) {
        open(id);
        return;
      }
      if (registrations.current.get(editingId)?.isDirty()) {
        setPendingId(id);
        return;
      }
      open(id);
    },
    [editingId, open],
  );

  const saveAndContinue = useCallback(() => {
    const current = editingId;
    const next = pendingId;
    if (current === null || next === null) return;
    const registration = registrations.current.get(current);
    if (!registration) {
      open(next);
      return;
    }
    setSaving(true);
    void registration
      .save()
      .then((ok) => {
        // Salvamento reprovado (enunciado vazio, 422) mantém a pessoa na questão
        // atual, com o erro à vista — em vez de levá-la para outra deixando o
        // erro atrás.
        if (ok) open(next);
        else setPendingId(null);
      })
      .finally(() => setSaving(false));
  }, [editingId, pendingId, open]);

  return {
    editingId,
    requestEdit,
    close: useCallback(() => open(null), [open]),
    register,
    pendingId,
    saving,
    dismiss: useCallback(() => setPendingId(null), []),
    discardAndContinue: useCallback(() => open(pendingId), [open, pendingId]),
    saveAndContinue,
  };
}
